<script lang="ts" setup>
import { NBadge, type MenuOption, type DropdownOption } from 'naive-ui'
import MUserPanel from './m/MUserPanel.vue'
import MLink from './m/MLink.vue'
import MIcon from './m/MIcon.vue'
import AppBarUserInfoCard from '~/components/user/AppBarUserInfoCard.vue'

const { scrolled, scrollY } = useScrollState()

const showLoginForm = ref(false)
const type = ref('signin')

const screen = useScreen()
const { data: userInfo, refresh } = useAuth()

const searchText = ref('')

const lightoff = useState<boolean>('player_light_off')
const fullscreen = useState<boolean>('player_fullscreen')

const showReportForm = ref(false)
const reportResourceId = ref<number>()
const reportResourceType = ref<ResourceType>()

onMounted(() => {
  notifyInterval()

  useEmitter().on('requestLogin', () => {
    login()
  })
  useEmitter().on('openReportForm', ({ resourceId, resourceType }) => {
    showReportForm.value = true
    reportResourceId.value = resourceId
    reportResourceType.value = resourceType
  })

  useEmitter().on('openCommentDetail', ({ commentId }) => {
    showCommentDetail.value = true
    originCommentId.value = commentId
  })

  useEmitter().on('openForwardForm', ({ resourceId, resourceType }) => {
    showForwardForm.value = true
    forwardResourceId.value = resourceId
    forwardResourceType.value = resourceType
  })
  useEmitter().on('openRewardForm', ({ resourceId, resourceType }) => {
    showRewardForm.value = true
    rewardResourceId.value = resourceId
    rewardResourceType.value = resourceType
  })
  useEmitter().on('openFavoriteForm', ({ resourceId, resourceType }) => {
    showFavoriteForm.value = true
    favoriteResourceId.value = resourceId
    favoriteResourceType.value = resourceType
  })
})

const isSearchPage = computed(() => useRoute().path.startsWith('/search'))
const isIndexPage = computed(
  () =>
    useRoute().path === '/' ||
    useRoute().path === '/newIndex' ||
    useRoute().path.startsWith('/v/') ||
    useRoute().path.startsWith('/category/')
)

const { data: banner } = await useAsyncData('fetchBanner:index', () =>
  fetchBanner('index')
)

const { data: notifyCount, refresh: refreshNotifyCount } = useNotify()

const totalNotifyCount = computed(() =>
  Object.values(notifyCount.value).reduce((a, b) => a + b, 0)
)

async function notifyInterval() {
  await refreshNotifyCount()
  setTimeout(notifyInterval, 1000 * 30)
}

function goTo(item: Carousel) {
  if (item.type === 'link') {
    window.open(item.url, '_blank')
  } else {
    window.open(`/${item.resourceType}/${item.resourceId}`, '_blank')
  }
}

const showCommentDetail = ref(false)
const originCommentId = ref<number>()

const showForwardForm = ref(false)
const forwardResourceId = ref<number>()
const forwardResourceType = ref<ResourceType>()

const showRewardForm = ref(false)
const rewardResourceId = ref<number>()
const rewardResourceType = ref<ResourceType>()

const showFavoriteForm = ref(false)
const favoriteResourceId = ref<number>()
const favoriteResourceType = ref<ResourceType>()

function login() {
  type.value = 'signin'
  showLoginForm.value = true
}

function register() {
  type.value = 'signup'
  showLoginForm.value = true
}

function closeLoginForm() {
  showLoginForm.value = false
}

function closeReportForm() {
  showReportForm.value = false
}

function closeForwardForm() {
  showForwardForm.value = false
}

function closeRewardForm() {
  showRewardForm.value = false
}

function closeFavoriteForm() {
  showFavoriteForm.value = false
}

function renderCustomHeader() {
  return h(MUserPanel)
}

function renderIcon(icon: string) {
  return () =>
    h(MIcon, {
      icon: icon
    })
}

function renderDropdownLabel(option: DropdownOption) {
  return h(
    MLink,
    {
      to: option.key?.toString() || ''
    },
    {
      default: () =>
        h(
          NBadge,
          {
            class: 'm-header-message-option',
            value: (notifyCount.value as any)[option.count as any] || 0,
            max: 99,
            offset: [-6, 23]
          },
          {
            default: () => [
              h(MIcon, {
                icon: option.icons as string
              }),
              h(
                'span',
                {
                  class: 'm-header-message-option__text'
                },
                {
                  default: () => option.label
                }
              )
            ]
          }
        )
    }
  )
}

const messageOptions: MenuOption[] = [
  {
    label: '我的私信',
    icons: 'lucide-mail',
    key: '/message/whisper',
    count: 'message'
  },
  {
    label: '回复我的',
    icons: 'ni ni-comment',
    key: '/message/reply',
    count: 'comment'
  },
  {
    label: '提及我的',
    icons: 'lucide-at-sign',
    key: '/message/mention',
    count: 'mention'
  },
  {
    label: '收到的赞',
    icons: 'lucide-thumbs-up',
    key: '/message/like',
    count: 'like'
  },
  {
    label: '系统通知',
    icons: 'lucide-bell',
    key: '/message/system',
    count: 'system'
  }
]

function onCreate() {
  window.open('/create', '_blank')
}
</script>

<template>
  <nav
    class="m-header"
    :class="{
      index: isIndexPage,
      scrolled,
      lightoff,
      fullscreen
    }"
  >
    <nuxt-link
      v-if="userInfo.user?.state === 2"
      :to="`/member/${userInfo.user.id}/deleteAccount`"
      class="m-header__warning"
      no-prefetch
    >
      帐号已申请注销，如需取消注销，请单击此处进行下一步操作
    </nuxt-link>
    <div class="m-header__container">
      <div class="m-header__left">
        <m-logo class="m-header__logo" />
        <m-logo mini class="m-header__logo--mini" />
        <m-space gap="20px">
          <m-link to="/" title="首页" class="download-app link"> 首页</m-link>

          <m-link to="/download" title="APP下载" class="download-app link">
            下载APP
          </m-link>
        </m-space>
      </div>
      <div class="m-header__mid">
        <m-search-input v-if="!isSearchPage" v-model="searchText" />
      </div>
      <div class="m-header__right">
        <m-space v-if="userInfo?.login" align="center" gap="20px">
          <m-link to="/member/sign" title="签到" class="link">签到</m-link>
          <m-link to="/timeline/follow" title="动态" class="link">动态</m-link>
          <m-link to="/member/history" title="历史" class="link">历史</m-link>
          <n-dropdown
            :z-index="60000"
            trigger="click"
            size="huge"
            :options="messageOptions"
            :render-label="renderDropdownLabel"
          >
            <div class="link">
              消息
              <n-badge :value="totalNotifyCount" :max="99" :offset="[8, -10]" />
            </div>
          </n-dropdown>
          <n-popover
            :z-index="60000"
            trigger="click"
            size="huge"
            placement="bottom"
            content-class="padding:0"
          >
            <template #trigger>
              <m-avatar
                :id="userInfo.user.id"
                size="36"
                :src="userInfo.user.avatar"
              />
            </template>
            <app-bar-user-info-card />
          </n-popover>
          <n-button
            type="primary"
            size="small"
            class="upload"
            @click="onCreate"
          >
            <template #icon>
              <div class="i-lucide:upload"></div>
            </template>
            投稿
          </n-button>
        </m-space>
        <m-space v-else align="center" gap="20px">
          <m-link to="/timeline/newest" title="动态" class="link">动态</m-link>
          <m-link nested title="注册" class="link" @click="register">
            注册
          </m-link>
          <n-button
            class="button signin"
            type="primary"
            size="small"
            @click="login"
          >
            登录
          </n-button>
        </m-space>
      </div>
    </div>
    <div class="m-header__container--mobile">
      <m-logo block />
    </div>
    <div
      v-if="isIndexPage && !screen.xs"
      class="m-header__banner--index"
      :style="{
        opacity: (100 - scrollY) / 100,
        height: 172 - scrollY + 'px'
      }"
    >
      <MImg
        v-if="banner"
        :src="banner.image"
        :width="0"
        @click="goTo(banner!)"
      />
      <div class="m-header__banner--index__backdrop"></div>
    </div>
    <m-modal v-model="showLoginForm" size="sm">
      <m-login-form
        v-model="type"
        @success="closeLoginForm"
        @cancel="closeLoginForm"
      />
    </m-modal>
    <m-modal v-model="showReportForm" size="sm">
      <m-report-form
        :resource-id="reportResourceId"
        :resource-type="reportResourceType"
        @success="closeReportForm"
        @cancel="closeReportForm"
      />
    </m-modal>
    <m-modal v-model="showForwardForm" size="sm">
      <m-forward-form
        :resource-id="forwardResourceId"
        :resource-type="forwardResourceType"
        @success="closeForwardForm"
        @cancel="closeForwardForm"
      />
    </m-modal>
    <m-modal v-model="showRewardForm" size="sm">
      <m-reward-form
        :resource-id="rewardResourceId"
        :resource-type="rewardResourceType"
        @success="closeRewardForm"
        @cancel="closeRewardForm"
      />
    </m-modal>
    <m-modal v-model="showFavoriteForm" size="sm">
      <m-favorite-form
        :resource-id="favoriteResourceId"
        :resource-type="favoriteResourceType"
        @success="closeFavoriteForm"
        @cancel="closeFavoriteForm"
      />
    </m-modal>
    <m-modal v-model="showCommentDetail" size="lg">
      <m-comment-detail :comment-id="originCommentId" />
    </m-modal>
  </nav>
</template>

<style lang="scss">
.m-header-message-option {
  padding-left: 4px;
  padding-right: 24px;
  color: var(--color-gray-700);

  &__text {
    font-size: 13px;
    margin-left: 4px;
  }
}
</style>

<style lang="scss" scoped>
.m-header {
  left: 0;
  top: 0;
  right: 0;
  padding: 0 12px;
  margin: auto;
  height: 72px;
  background: #fff;
  box-shadow: none;
  position: fixed;
  z-index: 30000;
  transition: background 0.3s ease-in-out;
  display: flex;
  flex-direction: column;

  &__banner--index {
    max-width: 1920px;
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    right: 0;
    margin: 0 auto;
    height: 172px;
    cursor: pointer;
    overflow: hidden;

    img {
      width: 100%;
      max-width: 1920px;
      height: 172px;
    }

    &__backdrop {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      right: 0;
      height: 72px;
      background: linear-gradient(
        to bottom,
        rgba($color: #000000, $alpha: 0.6),
        rgba($color: #000000, $alpha: 0)
      );
    }
  }

  .link {
    cursor: pointer;
  }

  .link:hover {
    color: var(--color-blue-500);
  }

  &__logo {
    background: var(--color-primary);

    &--mini {
      display: none;
    }
  }

  &__warning {
    width: 100%;
    background-color: var(--color-red-400);
    text-align: center;
    color: white;
    margin-bottom: 4px;
    text-decoration: none;
  }

  &__container {
    position: relative;
    width: 100%;
    max-width: 1400px;
    padding: 0 20px;
    margin: auto;
    display: flex;
    justify-content: center;
    align-items: center;

    &--mobile {
      display: none;
      width: 100%;
      height: 100%;
      justify-content: center;
      align-items: center;
    }
  }

  &__left {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    left: 16px;
  }

  &__mid {
    width: 30%;
  }

  &__right {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: absolute;
    right: 16px;

    .button {
      //width: 80px;
    }
  }

  &__search {
    width: 100%;
  }
}

.m-header.index {
  background: transparent;

  .link {
    color: var(--color-gray-50);
  }

  .m-header {
    &__logo {
      background: var(--bg-primary);
    }
  }
}

.m-header.scrolled {
  background: #202233;
  box-shadow:
    0 1px 2px -1px rgb(0, 0, 0, 10%),
    0px 2px 3px 0px rgb(0, 0, 0, 7%),
    0px 1px 5px 0px rgb(0, 0, 0, 6%);

  .link {
    color: var(--color-gray-300);
  }

  .link:hover {
    color: #fff;
  }

  .m-header {
    &__logo {
      background: var(--bg-primary);
    }
  }
}

.m-header.lightoff {
  background: rgba($color: #000000, $alpha: 0);
  z-index: 1000 !important;
}

.m-header.fullscreen {
  z-index: 1000;
}

@media screen and (max-width: 600px) {
  .m-header {
    padding: 0;

    &__container {
      min-width: 90vw;
    }

    .link,
    .upload,
    .signup {
      display: none;
    }

    &__mid {
      width: 50vw;
    }

    &__logo {
      display: none;

      &--mini {
        display: block;
      }
    }
  }
  .m-header.scrolled {
    background: #fff;
  }
}
</style>
